import { supabase } from '@/integrations/supabase/client';
import { ApiError, ApiResult, ok, err } from './errors';

/**
 * Configuration for API operations
 */
interface ApiConfig {
  retries?: number;
  retryDelay?: number;
}

const DEFAULT_CONFIG: ApiConfig = {
  retries: 2,
  retryDelay: 1000,
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Determines if an error is retryable
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof ApiError) {
    // Don't retry auth errors or not found
    if (['UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND'].includes(error.code)) {
      return false;
    }
  }
  
  // Retry network errors and 5xx status codes
  if (error instanceof Error && error.message.includes('network')) {
    return true;
  }
  
  return true;
}

/**
 * Execute an async operation with retry logic
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  config: ApiConfig = DEFAULT_CONFIG
): Promise<T> {
  const { retries = 2, retryDelay = 1000 } = config;
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      if (attempt < retries && isRetryableError(error)) {
        console.warn(`API call failed, retrying (${attempt + 1}/${retries})...`, error);
        await sleep(retryDelay * (attempt + 1)); // Exponential backoff
      } else {
        throw error;
      }
    }
  }

  throw lastError;
}

/**
 * Wrap a Supabase query with consistent error handling
 */
export async function query<T>(
  queryFn: () => Promise<{ data: T | null; error: { message: string; code?: string; details?: unknown } | null }>,
  config?: ApiConfig
): Promise<ApiResult<T>> {
  try {
    const result = await withRetry(queryFn, config);
    
    if (result.error) {
      return err(ApiError.fromSupabaseError(result.error));
    }
    
    if (result.data === null) {
      return err(ApiError.notFound());
    }
    
    return ok(result.data);
  } catch (error) {
    console.error('API query failed:', error);
    
    if (error instanceof ApiError) {
      return err(error);
    }
    
    return err(ApiError.networkError());
  }
}

/**
 * Wrap a Supabase mutation (insert/update/delete) with consistent error handling
 */
export async function mutate<T>(
  mutationFn: () => Promise<{ data: T | null; error: { message: string; code?: string; details?: unknown } | null }>,
  config?: ApiConfig
): Promise<ApiResult<T>> {
  try {
    const result = await withRetry(mutationFn, config);
    
    if (result.error) {
      return err(ApiError.fromSupabaseError(result.error));
    }
    
    return ok(result.data as T);
  } catch (error) {
    console.error('API mutation failed:', error);
    
    if (error instanceof ApiError) {
      return err(error);
    }
    
    return err(ApiError.networkError());
  }
}

/**
 * Check if user is authenticated before making a request
 */
export async function requireAuth(): Promise<ApiResult<string>> {
  const { data: { user }, error } = await supabase.auth.getUser();
  
  if (error || !user) {
    return err(ApiError.unauthorized());
  }
  
  return ok(user.id);
}

/**
 * Export the supabase client for direct access when needed
 */
export { supabase };
